import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowUp,
  X,
  Sparkles,
  Upload,
  Loader2,
  Plus,
  Eraser,
  Wand2,
  Maximize2,
  Image as ImageIcon,
  Type,
  Layers,
  ZoomIn,
  Paintbrush,
  ChevronLeft,
  ChevronRight,
  Video,
  Mic,
  Check
} from 'lucide-react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { deepgramService, TranscriptionResult } from '@/services/deepgramService';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { theme } from '@/lib/theme';
import { supabase } from '@/integrations/supabase/client';
import { DrawCanvasModal } from '@/components/canvas/DrawCanvasModal';
import EditIcon from "../../../packages/svgs/EditIcon";

const loaderStyles = `
  .chitra-loader {
    height: 60px;
    aspect-ratio: 1;
    padding: 10px;
    border-radius: 20px;
    box-sizing: border-box;
    position: relative;
    mask: conic-gradient(#000 0 0) content-box, conic-gradient(#000 0 0);
    -webkit-mask: conic-gradient(#000 0 0) content-box, conic-gradient(#000 0 0);
    -webkit-mask-composite: source-out;
    mask-composite: exclude;
    filter: blur(0);
    animation: l4-0 1.2s linear infinite alternate;
  }
  
  /* Fade in animation */
  .fade-in-text {
    animation: fadeIn 0.3s ease-out;
  }
  
  @keyframes fadeIn {
    from { 
      opacity: 0;
      transform: translateY(2px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .chitra-loader:before {
    content: "";
    position: absolute;
    inset: 0;
    background: repeating-conic-gradient(#0000 0 5%,#C02942,#0000 20% 50%);
    animation: l4-1 1s linear infinite;
  }
  @keyframes l4-0 {
    to { border-radius: 50% }
  }
  @keyframes l4-1 {
    to { transform: rotate(.5turn) }
  }

  .chitra-attachments-carousel {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .chitra-attachments-carousel::-webkit-scrollbar {
    display: none;
  }
`;

interface ChitraInputAreaProps {
  prompt: string;
  setPrompt: (value: string) => void;
  isGenerating: boolean;
  isEditingImage: boolean;
  isEnhancingPrompt: boolean;
  isEditMode: boolean;
  attachedImages: string[];
  selectedAspectRatio: string;
  showAspectRatios: boolean;
  setShowAspectRatios: (show: boolean) => void;
  isDarkMode: boolean;
  onGenerate: () => void;
  onEditImage: () => void;
  onEnhancePrompt: () => void;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearAttachedImage: () => void;
  onRemoveAttachedImage?: (index: number) => void;
  onUpdateAttachedImage?: (index: number, dataUrl: string) => void;
  onAddAttachedImage?: (dataUrl: string) => void;
  onAspectRatioSelect: (ratio: string) => void;
  onToolProcessing?: (isProcessing: boolean, toolName: string) => void;
  onToolComplete?: () => void;
  onVideoModeClick?: () => void;
  onGenerateVideo?: () => void;
  isVideoMode?: boolean;
  videoAvatarPreviewUrl?: string;
  videoBackgroundUrl?: string;
  onExitVideoMode?: () => void;
}

const aspectRatios = [
  { value: "1:1", label: "Square (1:1)" },
  { value: "3:2", label: "Classic (3:2)" },
  { value: "2:3", label: "Portrait (2:3)" },
  { value: "16:9", label: "Wide (16:9)" },
  { value: "9:16", label: "Portrait (9:16)" },
  { value: "21:9", label: "Ultrawide (21:9)" },
  { value: "4:3", label: "Standard (4:3)" },
  { value: "3:4", label: "Portrait (3:4)" },
];

export const ChitraInputArea: React.FC<ChitraInputAreaProps> = ({
  prompt,
  setPrompt,
  isGenerating,
  isEditingImage,
  isEnhancingPrompt,
  isEditMode,
  attachedImages,
  selectedAspectRatio,
  showAspectRatios,
  setShowAspectRatios,
  isDarkMode,
  onGenerate,
  onEditImage,
  onEnhancePrompt,
  onImageUpload,
  onClearAttachedImage,
  onRemoveAttachedImage,
  onUpdateAttachedImage,
  onAddAttachedImage,
  onAspectRatioSelect,
  onToolProcessing,
  onToolComplete,
  onVideoModeClick,
  onGenerateVideo,
  isVideoMode = false,
  videoAvatarPreviewUrl,
  videoBackgroundUrl,
  onExitVideoMode,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [ambientGradient, setAmbientGradient] = useState<string>("");
  const [showLeftMenu, setShowLeftMenu] = useState<boolean>(false);
  const leftMenuRef = useRef<HTMLDivElement>(null);
  const [textareaHeight, setTextareaHeight] = useState<number>(20);
  const [isRemovingBgAttach, setIsRemovingBgAttach] = useState(false);
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [showQuickTools, setShowQuickTools] = useState(false);
  const [isProcessingTool, setIsProcessingTool] = useState(false);
  const [processingToolName, setProcessingToolName] = useState('');
  const [showToolOptions, setShowToolOptions] = useState<string | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const quickToolsRef = useRef<HTMLDivElement>(null);
  const [activeToolMode, setActiveToolMode] = useState<{
    tool: 'replace-background' | null;
    name: string;
    icon: React.ReactNode;
  } | null>(null);
  const [showCanvas, setShowCanvas] = useState(false);
  const [canvasInitialImage, setCanvasInitialImage] = useState<string | null>(null);
  const [canvasTargetIndex, setCanvasTargetIndex] = useState<number | null>(null);
  const { toast } = useToast();

  // Speech to text states
  const [isRecording, setIsRecording] = useState(false);
  const [recordedTranscript, setRecordedTranscript] = useState('');
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const finalTranscriptRef = useRef<string>('');
  const recordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMicLoading, setIsMicLoading] = useState(false);

  const isExpanded = true;

  const isMobile = useIsMobile();
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const [mobileDrawerView, setMobileDrawerView] = useState<'main' | 'quicktools'>('main');
  const [isComposerVisible, setIsComposerVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsComposerVisible(true), 1000);
    return () => clearTimeout(t);
  }, []);

  const verticalFadeStyle: React.CSSProperties = {
    WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
    maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
  };

  // Tiny aspect ratio icon renderer
  const AspectIcon: React.FC<{ value: string; className?: string }> = ({ value, className }) => {
    // Base box 24x24, draw inner rect to represent aspect
    const size = 24;
    let w = 16, h = 16; // default square
    switch (value) {
      case "16:9":
        w = 20; h = 12; break;
      case "21:9":
        w = 22; h = 10; break; // extra-wide cinematic
      case "9:16":
        w = 12; h = 20; break;
      case "3:2":
        w = 20; h = 13; break; // approx
      case "2:3":
        w = 13; h = 20; break; // approx
      case "4:3":
        w = 20; h = 15; break;
      case "3:4":
        w = 15; h = 20; break;
      case "1:1":
      default:
        w = 16; h = 16; break;
    }
    const x = (size - w) / 2;
    const y = (size - h) / 2;
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={className} aria-hidden>
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          rx={2}
          ry={2}
          stroke="currentColor"
          strokeWidth={2}
          fill="none"
        />
      </svg>
    );
  };

  // Extract dominant colors from image for ambient mode (adaptive + subtle)
  const extractImageColors = React.useCallback((imageUrl: string) => {
    const img = document.createElement('img');
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const size = 50;
      canvas.width = size;
      canvas.height = size;
      ctx.drawImage(img, 0, 0, size, size);

      try {
        const imageData = ctx.getImageData(0, 0, size, size);
        const data = imageData.data;
        const colors: { r: number; g: number; b: number; count: number }[] = [];

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const alpha = data[i + 3];
          if (alpha < 128) continue;
          const threshold = 30;
          let found = false;
          for (const color of colors) {
            if (
              Math.abs(color.r - r) < threshold &&
              Math.abs(color.g - g) < threshold &&
              Math.abs(color.b - b) < threshold
            ) {
              color.count++;
              found = true;
              break;
            }
          }
          if (!found) colors.push({ r, g, b, count: 1 });
        }

        colors.sort((a, b) => b.count - a.count);

        const dist = (c1: any, c2: any) => {
          const dr = c1.r - c2.r;
          const dg = c1.g - c2.g;
          const db = c1.b - c2.b;
          return Math.sqrt(dr * dr + dg * dg + db * db);
        };

        if (colors.length >= 1) {
          const primary = colors[0];
          let secondary = colors.find((c) => dist(c, primary) >= 60) || colors[1] || colors[0];

          let a1 = isDarkMode ? 0.35 : 0.22;
          let a2 = isDarkMode ? 0.25 : 0.16;
          let a3 = isDarkMode ? 0.15 : 0.10;

          const lum = (c: any) => (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
          const lp = lum(primary);
          const ls = lum(secondary || primary);
          const avgLum = (lp + ls) / 2;
          if (avgLum < 0.25 || avgLum > 0.85) {
            a1 = Math.min(a1 + 0.08, 0.5);
            a2 = Math.min(a2 + 0.06, 0.45);
            a3 = Math.min(a3 + 0.05, 0.35);
          }

          let gradient: string;
          if (secondary) {
            gradient = `linear-gradient(135deg,
              rgba(${primary.r}, ${primary.g}, ${primary.b}, ${a1}) 0%,
              rgba(${secondary.r}, ${secondary.g}, ${secondary.b}, ${a2}) 50%,
              rgba(${primary.r}, ${primary.g}, ${primary.b}, ${a3}) 100%)`;
          } else {
            gradient = `linear-gradient(135deg,
              rgba(${primary.r}, ${primary.g}, ${primary.b}, ${a1}) 0%,
              rgba(${primary.r}, ${primary.g}, ${primary.b}, ${a3}) 100%)`;
          }
          setAmbientGradient(gradient);
        }
      } catch (error) {
        const fallback = isDarkMode
          ? `linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.10) 100%)`
          : `linear-gradient(135deg, rgba(0,0,0,0.14) 0%, rgba(0,0,0,0.08) 100%)`;
        setAmbientGradient(fallback);
      }
    };

    img.onerror = () => {
      const fallback = isDarkMode
        ? `linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.10) 100%)`
        : `linear-gradient(135deg, rgba(0,0,0,0.14) 0%, rgba(0,0,0,0.08) 100%)`;
      setAmbientGradient(fallback);
    };
    img.src = imageUrl;
  }, [isDarkMode]);

  // Extract colors when at least one image is attached (use the first)
  useEffect(() => {
    const first = attachedImages && attachedImages.length > 0 ? attachedImages[0] : null;
    if (first) {
      extractImageColors(first);
    } else {
      setAmbientGradient("");
    }
  }, [attachedImages, extractImageColors, isDarkMode]);


  const clearRecordingTimeout = useCallback(() => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearRecordingTimeout();
      try {
        // Ensure any active Deepgram connection and microphone stream are fully stopped on unmount
        deepgramService.stopRecording();
      } catch (e) {
        console.error('Error stopping recording on unmount:', e);
      }
    };
  }, [clearRecordingTimeout]);

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('getUserMedia not supported');
        setTranscriptionError('Audio recording not supported in this browser');
        return;
      }

      setIsRecording(true);
      setRecordedTranscript('');
      setTranscriptionError(null);
      finalTranscriptRef.current = '';
      clearRecordingTimeout();

      // Start Deepgram transcription
      await deepgramService.startRecording(
        (result: TranscriptionResult) => {
          if (result.isFinal) {
            finalTranscriptRef.current += (finalTranscriptRef.current ? ' ' : '') + result.transcript;
            setRecordedTranscript(finalTranscriptRef.current);
          } else {
            const interim = finalTranscriptRef.current + (finalTranscriptRef.current ? ' ' : '') + result.transcript;
            setRecordedTranscript(interim);
          }
          setTranscriptionError(null);
        },
        (error: string) => {
          console.error('Transcription error:', error);
          setTranscriptionError(error);
        }
      );

      recordingTimeoutRef.current = setTimeout(() => {
        setTranscriptionError('Recording limit reached (50s)');
        stopRecording();
      }, 50000);
    } catch (error) {
      console.error('Error starting recording:', error);
      setIsRecording(false);
      setTranscriptionError('Could not start recording');
      clearRecordingTimeout();
    }
  };

  const stopRecording = useCallback(() => {
    try {
      clearRecordingTimeout();

      // Stop Deepgram service
      deepgramService.stopRecording();

      // Preserve the final transcript before any state changes
      const finalTranscript = finalTranscriptRef.current.trim();

      // Reset recording state immediately
      setIsRecording(false);

      // Insert the final transcript into the input AFTER resetting recording state
      setTimeout(() => {
        if (finalTranscript && finalTranscript.length > 0) {
          setPrompt(prompt + (prompt ? ' ' : '') + finalTranscript);
        } else if (!transcriptionError) {
          console.warn('No transcript available');
        }

        // Clear transcript states after inserting into input
        setRecordedTranscript('');
        finalTranscriptRef.current = '';
      }, 0);
    } catch (error) {
      console.error('Error stopping recording:', error);
    }
  }, [prompt, transcriptionError]);

  const handleStartRecording = useCallback(async () => {
    setIsMicLoading(true);
    try {
      await startRecording();
    } finally {
      setTimeout(() => setIsMicLoading(false), 500);
    }
  }, [startRecording]);

  const handleStopRecording = useCallback(() => {
    setIsMicLoading(true);
    try {
      stopRecording();
    } finally {
      setTimeout(() => setIsMicLoading(false), 500);
    }
  }, [stopRecording]);

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const computed = window.getComputedStyle(textarea);
      const paddingTop = parseFloat(computed.paddingTop) || 0;
      const paddingBottom = parseFloat(computed.paddingBottom) || 0;
      const desiredOuter = Math.min(textarea.scrollHeight, 140);
      let heightToSet = desiredOuter;
      if (computed.boxSizing === 'border-box') {
        heightToSet = desiredOuter - paddingTop - paddingBottom;
      }
      textarea.style.height = `${heightToSet}px`;
      setTextareaHeight(desiredOuter);
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [prompt]);

  // Ensure compact height on initial mount
  useEffect(() => {
    adjustTextareaHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeMobileDrawer = React.useCallback(() => {
    setShowMobileDrawer(false);
    setMobileDrawerView('main');
  }, []);

  const closeMenus = React.useCallback(() => {
    setShowLeftMenu(false);
    setShowQuickTools(false);
    setShowToolOptions(null);
    setShowAspectRatios(false);
    closeMobileDrawer();
  }, [setShowAspectRatios, closeMobileDrawer]);

  const exitToolMode = () => {
    setActiveToolMode(null);
    setPrompt('');
  };

  useEffect(() => {
    if (isGenerating || isEditingImage || isProcessingTool) {
      closeMenus();
    }
  }, [isGenerating, isEditingImage, isProcessingTool, closeMenus]);

  // When aspect ratio dropdown closes, also close tools/options
  useEffect(() => {
    if (!showAspectRatios) {
      setShowQuickTools(false);
      setShowToolOptions(null);
    }
  }, [showAspectRatios]);

  const closeCanvas = React.useCallback(() => {
    setShowCanvas(false);
    setCanvasInitialImage(null);
    setCanvasTargetIndex(null);
  }, []);

  // Handle paste from clipboard - only allow PNG/JPG images
  const handlePaste = React.useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const dt = e.clipboardData;
    if (!dt) return;

    // Build list of image files from clipboard
    const files: File[] = [];
    if (dt.files && dt.files.length > 0) {
      for (let i = 0; i < dt.files.length; i++) files.push(dt.files[i]);
    } else if (dt.items && dt.items.length > 0) {
      for (let i = 0; i < dt.items.length; i++) {
        const item = dt.items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
    }

    if (files.length === 0) return;

    // Check attachment limit (max 4)
    const maxAttachments = 6;
    const remaining = Math.max(0, maxAttachments - (attachedImages?.length || 0));
    if (remaining === 0) {
      toast({ title: 'Attachment limit reached', description: 'You can attach up to 6 images.', variant: 'destructive' });
      e.preventDefault();
      return;
    }

    // Only allow PNG and JPG
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const maxImageSize = 10 * 1024 * 1024; // 10MB

    let addedCount = 0;
    for (const file of files.slice(0, remaining)) {
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        toast({ title: 'Unsupported format', description: 'Only PNG and JPG images are allowed.', variant: 'destructive' });
        continue;
      }
      if (file.size > maxImageSize) {
        toast({ title: 'Image too large', description: 'Please use images up to 10 MB.', variant: 'destructive' });
        continue;
      }

      // Convert to data URL and add
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl && onAddAttachedImage) {
          onAddAttachedImage(dataUrl);
        }
      };
      reader.readAsDataURL(file);
      addedCount++;
    }

    if (addedCount > 0) {
      e.preventDefault();
    }
  }, [attachedImages, onAddAttachedImage, toast]);

  const openCanvas = React.useCallback((initialImage: string | null = null, targetIndex: number | null = null) => {
    if (initialImage === null && attachedImages.length >= 6) {
      toast({
        title: 'Attachment limit reached',
        description: 'Remove an image before adding another drawing.',
        variant: 'destructive'
      });
      return;
    }
    setCanvasInitialImage(initialImage);
    setCanvasTargetIndex(targetIndex);
    setShowCanvas(true);
    closeMenus();
  }, [attachedImages.length, closeMenus, toast]);

  const handleCanvasDone = React.useCallback((dataUrl: string) => {
    if (canvasTargetIndex !== null) {
      if (onUpdateAttachedImage) {
        onUpdateAttachedImage(canvasTargetIndex, dataUrl);
      } else {
        toast({ title: 'Edit unavailable', description: 'Image editing callback missing.', variant: 'destructive' });
      }
    } else if (onAddAttachedImage) {
      onAddAttachedImage(dataUrl);
    } else {
      toast({ title: 'Add unavailable', description: 'Image add callback missing.', variant: 'destructive' });
    }
    closeCanvas();
  }, [canvasTargetIndex, closeCanvas, onAddAttachedImage, onUpdateAttachedImage, toast]);

  const handleSubmit = async () => {
    if (submitLockRef.current || isLocalSubmitting || isGenerating || isEditingImage || isProcessingTool) return;

    submitLockRef.current = true;
    setIsLocalSubmitting(true);
    closeMenus();

    // If in tool mode, execute the tool with the prompt
    if (activeToolMode && activeToolMode.tool) {
      if (!prompt.trim()) {
        toast({
          title: 'Input Required',
          description: 'Please enter a description for the background.',
          variant: 'destructive'
        });
        submitLockRef.current = false;
        setIsLocalSubmitting(false);
        return;
      }
      await handleToolAction(activeToolMode.tool, { prompt: prompt.trim() });
      exitToolMode();
      // Release local lock shortly after tool submission triggers
      setTimeout(() => {
        submitLockRef.current = false;
        setIsLocalSubmitting(false);
      }, 300);
      return;
    }

    // Normal generate/edit/video flow
    if (isEditMode) {
      onEditImage();
    } else if (isVideoMode && onGenerateVideo) {
      onGenerateVideo();
    } else {
      onGenerate();
    }

    // Clear input immediately after sending
    setPrompt('');

    // Release local lock after a short debounce to block rapid taps until parent state updates
    setTimeout(() => {
      submitLockRef.current = false;
      setIsLocalSubmitting(false);
    }, 300);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!leftMenuRef.current) return;
      if (!(e.target instanceof Node)) return;
      if (!leftMenuRef.current.contains(e.target)) {
        setShowLeftMenu(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!quickToolsRef.current) return;
      if (!(e.target instanceof Node)) return;
      if (!quickToolsRef.current.contains(e.target)) {
        setShowQuickTools(false);
        setShowToolOptions(null);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const handleToolAction = async (
    tool: 'remove-bg' | 'uncrop' | 'replace-background' | 'remove-text' | 'commercial-photography' | 'upscale' | 'cleanup',
    options: any = {}
  ) => {
    if (isProcessingTool || !attachedImages || attachedImages.length === 0) return;

    closeMenus();

    try {
      setIsProcessingTool(true);
      const toolNames: Record<string, string> = {
        'remove-bg': 'Removing Background',
        'uncrop': 'Uncropping',
        'replace-background': 'Replacing Background',
        'remove-text': 'Removing Text',
        'commercial-photography': 'Commercial Photography',
        'upscale': 'Upscaling',
        'cleanup': 'Cleaning Up'
      };
      setProcessingToolName(toolNames[tool]);
      onToolProcessing?.(true, toolNames[tool]);

      const imgSrc = attachedImages[0];
      const userId = (await supabase.auth.getUser()).data.user?.id || null;

      const requestBody: any = {
        tool,
        imageBase64: imgSrc.startsWith('data:') ? imgSrc : undefined,
        imageUrl: !imgSrc.startsWith('data:') ? imgSrc : undefined,
        userId,
        saveToGallery: true,
        aspectRatio: selectedAspectRatio || '1:1',
        ...options
      };

      const { data, error } = await supabase.functions.invoke('image-tools', {
        body: requestBody
      });

      if (error || !data?.success) {
        throw new Error(data?.error || 'Tool processing failed');
      }

      toast({
        title: '',
        description: `${toolNames[tool]} completed.`
      });

      setShowQuickTools(false);
      setShowToolOptions(null);

      // Notify parent to refresh if needed (legacy path). For base64-only flow, parent may ignore.
      onToolComplete?.();
    } catch (err: any) {
      console.error('Tool error:', err);
      toast({
        title: 'Error',
        description: err?.message || 'Something went wrong.',
        variant: 'destructive'
      });
    } finally {
      setIsProcessingTool(false);
      setProcessingToolName('');
      onToolProcessing?.(false, '');
    }
  };

  return (
    <>
      <style>{loaderStyles}</style>
      <div
        className={cn(
          "fixed bottom-0 left-0 right-0 p-2 pb-1 z-20 mt-20 transition-all duration-500 ease-out",
          isComposerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        )}
      >
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <div className="relative flex flex-col">

              <div className="relative w-full">
                {/* Video Mode Indicator with previews */}
                {isVideoMode && (
                  <div className="mb-2 mx-2 w-fit flex items-center gap-2 px-2 py-1.5 rounded-xl border" style={{
                    backgroundColor: isDarkMode ? 'rgba(139, 92, 246, 0.08)' : 'rgba(139, 92, 246, 0.08)',
                    borderColor: isDarkMode ? 'rgba(139, 92, 246, 0.5)' : 'rgba(139, 92, 246, 0.5)'
                  }}>
                    <div className="flex items-center gap-2 pl-1 pr-2">
                      <Video className="w-4 h-4 text-white-500" />
                      <span className={cn(
                        "text-xs font-medium whitespace-nowrap",
                        isDarkMode ? "text-purple-300" : "text-purple-700"
                      )}>
                        Video Mode
                      </span>
                    </div>
                    {videoAvatarPreviewUrl && (
                      <div className="flex items-center gap-1 pr-1">
                        <img src={videoAvatarPreviewUrl} alt="Selected avatar" className="h-10 w-10 rounded-md object-cover ring-1 ring-black/10 dark:ring-white/10" />
                      </div>
                    )}
                    {videoBackgroundUrl && (
                      <div className="flex items-center gap-1 pr-1">
                        <div className="h-10 w-10 rounded-md overflow-hidden ring-1 ring-black/10 dark:ring-white/10">
                          <div className="h-full w-full bg-cover bg-center" style={{ backgroundImage: `url(${videoBackgroundUrl})` }} />
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => onExitVideoMode?.()}
                      className={cn(
                        "ml-1 p-1 rounded-md transition-colors",
                        isDarkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
                      )}
                      aria-label="Exit video mode"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {activeToolMode && (
                  <div className="mb-2 ml-2 w-fit flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                  }}>
                    {activeToolMode.icon}
                    <span className={cn(
                      "text-xs font-medium whitespace-nowrap",
                      isDarkMode ? "text-white/80" : "text-gray-700"
                    )}>
                      {activeToolMode.name}
                    </span>
                    <button
                      onClick={exitToolMode}
                      className={cn(
                        "ml-1 p-0.5 rounded-full transition-colors",
                        isDarkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
                      )}
                      aria-label="Exit tool mode"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}



                <div
                  className={cn(
                    "w-full flex flex-col border transition-[border-radius,box-shadow,background-color,border-color] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-[border-radius] rounded-[28px]",
                    isDarkMode
                      ? "bg-[rgba(37,37,37,0.6)] backdrop-blur-[22px] backdrop-saturate-200 border-white/15 ring-0 shadow-none"
                      : "bg-white/70 backdrop-blur-[22px] backdrop-saturate-200 border-black/10 ring-0 shadow-none"
                  )}
                >
                  {(attachedImages?.length ?? 0) > 0 && (
                    <div className="relative rounded-t-[28px] overflow-hidden">
                      <div className={cn(
                        "chitra-attachments-carousel mt-1 flex flex-row gap-3 px-3 pt-1 overflow-x-auto",
                        "snap-x snap-mandatory scroll-px-2",
                        "touch-pan-x"
                      )}>
                        {attachedImages.slice(0, 6).map((imgSrc, idx) => (
                          <div key={idx} className="group relative flex-shrink-0 snap-start">
                            <div className={cn(
                              "relative overflow-hidden rounded-2xl border",
                              isDarkMode ? "border-white/20" : "border-gray-200"
                            )}>
                              <div className="relative w-20 h-20 rounded-xl overflow-hidden">
                                <button
                                  type="button"
                                  className={cn(
                                    "w-full h-full rounded-xl overflow-hidden flex items-center justify-center",
                                    imgSrc === "LOADING" ? "bg-black/5 dark:bg-white/5 cursor-default" : "bg-cover bg-center"
                                  )}
                                  aria-label="View Preview image in full size"
                                  aria-haspopup="dialog"
                                  onClick={() => {
                                    if (imgSrc === "LOADING") return;
                                    const modal = document.createElement("div");
                                    modal.className =
                                      "fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4";
                                    modal.onclick = () => modal.remove();

                                    const img = document.createElement("img");
                                    img.src = imgSrc;
                                    img.className =
                                      "max-w-full max-h-full object-contain rounded-lg";

                                    modal.appendChild(img);
                                    document.body.appendChild(modal);
                                  }}
                                  style={imgSrc === "LOADING" ? undefined : { backgroundImage: `url(${imgSrc})` }}
                                >
                                  {imgSrc === "LOADING" && (
                                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-hidden="true" />
                                  )}
                                </button>
                              </div>
                            </div>

                            <div className="absolute -top-0 -right-0 flex gap-1">
                              <button
                                type="button"
                                onClick={() => openCanvas(imgSrc, idx)}
                                className={cn(
                                  "rounded-full p-1 border transition-colors shadow-lg",
                                  isDarkMode
                                    ? "bg-black/60 text-white border-white/20 hover:bg-black/75"
                                    : "bg-white text-gray-900 border-gray-300 hover:bg-gray-50",
                                  imgSrc === "LOADING" && "opacity-50 cursor-not-allowed"
                                )}
                                aria-label="Edit attachment"
                                disabled={imgSrc === "LOADING"}
                              >
                                <EditIcon className={cn("w-4 h-4", isDarkMode ? "text-white" : "text-gray-900")} />
                              </button>
                              <button
                                type="button"
                                onClick={() => (onRemoveAttachedImage ? onRemoveAttachedImage(idx) : onClearAttachedImage())}
                                className={cn(
                                  "rounded-full p-1 border transition-colors shadow-lg",
                                  isDarkMode
                                    ? "bg-black/60 text-white border-white/20 hover:bg-black/75"
                                    : "bg-white text-gray-900 border-gray-300 hover:bg-gray-50"
                                )}
                                aria-label="Remove attachment"
                              >
                                <X className={cn("w-4 h-3", isDarkMode ? "text-white" : "text-gray-900")} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div
                    className={cn(
                      "flex w-full gap-2 transition-[padding] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
                      "items-start px-4 pt-1.5"
                    )}
                  >
                    {isRecording ? (
                      <div className="w-full flex items-center min-h-[50px] px-4">
                        <div
                          key={recordedTranscript}
                          className={cn(
                            'text-base w-full fade-in-text',
                            isDarkMode ? 'text-white' : 'text-gray-900',
                            recordedTranscript && 'transition-all duration-200'
                          )}
                        >
                          {recordedTranscript ? (
                            <span className="inline-block">{recordedTranscript}</span>
                          ) : (
                            <span className={cn(
                              'italic fade-in-text',
                              isDarkMode ? 'text-gray-500' : 'text-gray-400'
                            )}>
                              Listening...
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 min-w-0" style={isExpanded ? verticalFadeStyle : undefined}>
                        <Textarea
                          ref={textareaRef}
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          onKeyDown={(e) => {
                            // Do not submit on Enter. Allow Enter to create a new line.
                            // Optionally: could add Ctrl/Cmd+Enter in the future.
                          }}
                          onPaste={handlePaste}
                          placeholder={
                            activeToolMode
                              ? "Describe the background you want..."
                              : isEditMode
                                ? "Describe the changes you want..."
                                : "Describe your image..."
                          }
                          disabled={isLocalSubmitting || isGenerating || isEditingImage || isProcessingTool}
                          className={cn(
                            "w-full resize-none bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm leading-relaxed box-border overflow-y-auto",
                            "py-2 px-0 min-h-[22px] max-h-[150px]",
                            isDarkMode ? "text-white placeholder:text-white/40" : "text-gray-900 placeholder:text-gray-400"
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <div className={cn(
                    "pt-2 px-3 pb-2 flex items-center justify-between"
                  )}>
                    <div className="flex items-center gap-2" ref={leftMenuRef}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "h-9 w-9 rounded-full flex items-center justify-center",
                          isDarkMode ? "hover:bg-white/10 text-white/80" : "hover:bg-gray-100 text-gray-800"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isMobile) {
                            setShowMobileDrawer(true);
                            setMobileDrawerView('main');
                          } else {
                            setShowLeftMenu((s) => !s);
                          }
                        }}
                        aria-label="More options"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "inline-flex gap-1.5 items-center justify-center whitespace-nowrap text-sm focus-visible:outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-default data-[disabled=true]:opacity-50 group/button relative px-3 py-2 h-9 rounded-full max-w-52 overflow-hidden font-semibold outline-none disabled:opacity-50",
                          isDarkMode
                            ? "bg-white/10 hover:bg-white/15 text-white"
                            : "bg-black/[0.04] hover:bg-black/[0.08] text-gray-900"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isMobile) {
                            setShowMobileDrawer(true);
                            setMobileDrawerView('main');
                          } else {
                            setShowLeftMenu(true);
                          }
                        }}
                        aria-label="Aspect ratio"
                      >
                        <AspectIcon value={selectedAspectRatio} className="w-4 h-4" />
                        <span className="text-xs font-medium">{selectedAspectRatio}</span>
                      </Button>

                      {showLeftMenu && (
                        <div
                          className={cn(
                            "absolute left-2 bottom-14 w-[200px] xs:w-[240px] rounded-2xl border z-[1000] overflow-visible",
                            isDarkMode
                              ? "bg-[#1f1f1f] border-[#2b2b2b] shadow-[0_8px_30px_rgb(0,0,0,0.6)]"
                              : "bg-white border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.15)]"
                          )}
                          role="menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="px-2 pb-2 mt-2">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => {
                                onImageUpload(e);
                                setShowLeftMenu(false);
                              }}
                              className="hidden"
                              id="chitra-image-upload-expanded"
                            />
                            <label
                              htmlFor="chitra-image-upload-expanded"
                              className={cn(
                                "flex items-center gap-3 px-3 py-1.5 rounded-xl cursor-pointer transition-colors",
                                isDarkMode
                                  ? "text-white hover:bg-white/10"
                                  : "text-gray-900 hover:bg-gray-100"
                              )}
                            >
                              <Upload className="w-4 h-4" />
                              <span className="text-sm">Upload image</span>
                            </label>

                            <button
                              type="button"
                              onClick={() => openCanvas(null, null)}
                              className={cn(
                                "mt-2 w-full flex items-center gap-3 px-3 py-1.5 rounded-xl text-left transition-colors",
                                isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100"
                              )}
                            >
                              <Paintbrush className="w-4 h-4" />
                              <span className="text-sm">Draw</span>
                            </button>

                            {onVideoModeClick && (
                              <button
                                type="button"
                                onClick={() => setShowVideoModal(true)}
                                className={cn(
                                  "flex items-center gap-3 px-3 py-1.5 rounded-xl w-full transition-colors mt-1",
                                  isDarkMode
                                    ? "text-white hover:bg-white/10"
                                    : "text-gray-900 hover:bg-gray-100"
                                )}
                              >
                                <Video className="w-4 h-4" />
                                <span className="text-sm">Video Mode</span>
                              </button>
                            )}

                            {attachedImages && attachedImages.length > 0 && (
                              <div className="relative" ref={quickToolsRef}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowQuickTools(!showQuickTools);
                                    setShowToolOptions(null);
                                  }}
                                  className={cn(
                                    "mt-2 w-full flex items-center gap-3 px-3 py-1.5 rounded-xl text-left transition-colors",
                                    isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100"
                                  )}
                                >
                                  <Wand2 className="w-4 h-4" />
                                  <span className="text-sm">Quick Tools</span>
                                </button>

                                {showQuickTools && (
                                  <div
                                    className={cn(
                                      "absolute left-0 right-0 -top-16 mt-2 w-[220px] rounded-2xl border z-[2000] overflow-hidden",
                                      "sm:left-full sm:right-auto sm:-top-4 sm:ml-2 sm:mt-0",
                                      isDarkMode
                                        ? "bg-[#1f1f1f] border-[#2b2b2b] shadow-[0_8px_30px_rgb(0,0,0,0.6)]"
                                        : "bg-white border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.15)]"
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="p-2 flex flex-col gap-1">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setShowToolOptions(null);
                                          setShowQuickTools(false);
                                        }}
                                        className={cn(
                                          "flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors sm:hidden mb-1",
                                          isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100"
                                        )}
                                      >
                                        <ChevronLeft className="w-4 h-4" />
                                        <span className="text-sm font-medium">Back</span>
                                      </button>

                                      {showToolOptions ? (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => setShowToolOptions(null)}
                                            className={cn(
                                              "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors mb-1",
                                              isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100"
                                            )}
                                          >
                                            <ChevronLeft className="w-4 h-4" />
                                            <span className="text-sm font-medium">Back</span>
                                          </button>

                                          {showToolOptions === 'uncrop' && (
                                            <>
                                              <div className={cn(
                                                "px-3 py-1 text-xs font-medium",
                                                isDarkMode ? "text-white/60" : "text-gray-500"
                                              )}>
                                                Uncrop Options
                                              </div>
                                              <button
                                                onClick={() => handleToolAction('uncrop', { extend_left: 200, extend_right: 200, extend_up: 200, extend_down: 200 })}
                                                disabled={isProcessingTool}
                                                className={cn(
                                                  "w-full text-left py-2 px-3 rounded-xl transition-colors",
                                                  isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                                  isProcessingTool && "opacity-50 cursor-not-allowed"
                                                )}
                                              >
                                                <div className="text-sm font-medium">Extend All</div>
                                                <div className={cn("text-xs", isDarkMode ? "text-white/60" : "text-gray-500")}>200px all sides</div>
                                              </button>
                                              <button
                                                onClick={() => handleToolAction('uncrop', { extend_left: 300, extend_right: 300 })}
                                                disabled={isProcessingTool}
                                                className={cn(
                                                  "w-full text-left py-2 px-3 rounded-xl transition-colors",
                                                  isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                                  isProcessingTool && "opacity-50 cursor-not-allowed"
                                                )}
                                              >
                                                <div className="text-sm font-medium">Extend Horizontal</div>
                                                <div className={cn("text-xs", isDarkMode ? "text-white/60" : "text-gray-500")}>300px left & right</div>
                                              </button>
                                              <button
                                                onClick={() => handleToolAction('uncrop', { extend_up: 300, extend_down: 300 })}
                                                disabled={isProcessingTool}
                                                className={cn(
                                                  "w-full text-left py-2 px-3 rounded-xl transition-colors",
                                                  isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                                  isProcessingTool && "opacity-50 cursor-not-allowed"
                                                )}
                                              >
                                                <div className="text-sm font-medium">Extend Vertical</div>
                                                <div className={cn("text-xs", isDarkMode ? "text-white/60" : "text-gray-500")}>300px top & bottom</div>
                                              </button>
                                            </>
                                          )}

                                          {showToolOptions === 'commercial' && (
                                            <>
                                              <div className={cn(
                                                "px-3 py-1 text-xs font-medium",
                                                isDarkMode ? "text-white/60" : "text-gray-500"
                                              )}>
                                                Commercial Photo Options
                                              </div>
                                              <button
                                                onClick={() => handleToolAction('commercial-photography', { background_color_choice: 'ffffff' })}
                                                disabled={isProcessingTool}
                                                className={cn(
                                                  "w-full text-left py-2 px-3 rounded-xl transition-colors",
                                                  isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                                  isProcessingTool && "opacity-50 cursor-not-allowed"
                                                )}
                                              >
                                                <div className="text-sm font-medium">White Background</div>
                                                <div className={cn("text-xs", isDarkMode ? "text-white/60" : "text-gray-500")}>Clean white backdrop</div>
                                              </button>
                                              <button
                                                onClick={() => handleToolAction('commercial-photography', { background_color_choice: '000000' })}
                                                disabled={isProcessingTool}
                                                className={cn(
                                                  "w-full text-left py-2 px-3 rounded-xl transition-colors",
                                                  isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                                  isProcessingTool && "opacity-50 cursor-not-allowed"
                                                )}
                                              >
                                                <div className="text-sm font-medium">Black Background</div>
                                                <div className={cn("text-xs", isDarkMode ? "text-white/60" : "text-gray-500")}>Dramatic black backdrop</div>
                                              </button>
                                              <button
                                                onClick={() => handleToolAction('commercial-photography', { background_color_choice: 'f5f5f5', light_theta: 30, shadow_darkness: 0.9 })}
                                                disabled={isProcessingTool}
                                                className={cn(
                                                  "w-full text-left py-2 px-3 rounded-xl transition-colors",
                                                  isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                                  isProcessingTool && "opacity-50 cursor-not-allowed"
                                                )}
                                              >
                                                <div className="text-sm font-medium">Studio Light</div>
                                                <div className={cn("text-xs", isDarkMode ? "text-white/60" : "text-gray-500")}>Professional lighting setup</div>
                                              </button>
                                            </>
                                          )}

                                          {showToolOptions === 'upscale' && (
                                            <>
                                              <div className={cn(
                                                "px-3 py-1 text-xs font-medium",
                                                isDarkMode ? "text-white/60" : "text-gray-500"
                                              )}>
                                                Upscale Quality
                                              </div>
                                              <button
                                                onClick={() => handleToolAction('upscale', { target_width: 2048, target_height: 2048 })}
                                                disabled={isProcessingTool}
                                                className={cn(
                                                  "w-full text-left py-2 px-3 rounded-xl transition-colors",
                                                  isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                                  isProcessingTool && "opacity-50 cursor-not-allowed"
                                                )}
                                              >
                                                <div className="text-sm font-medium">2K Quality</div>
                                                <div className={cn("text-xs", isDarkMode ? "text-white/60" : "text-gray-500")}>2048 x 2048 pixels</div>
                                              </button>
                                              <button
                                                onClick={() => handleToolAction('upscale', { target_width: 3840, target_height: 3840 })}
                                                disabled={isProcessingTool}
                                                className={cn(
                                                  "w-full text-left py-2 px-3 rounded-xl transition-colors",
                                                  isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                                  isProcessingTool && "opacity-50 cursor-not-allowed"
                                                )}
                                              >
                                                <div className="text-sm font-medium">4K Quality</div>
                                                <div className={cn("text-xs", isDarkMode ? "text-white/60" : "text-gray-500")}>3840 x 3840 pixels</div>
                                              </button>
                                            </>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => handleToolAction('remove-bg')}
                                            disabled={isProcessingTool}
                                            className={cn(
                                              "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors",
                                              isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                              isProcessingTool && "opacity-50 cursor-not-allowed"
                                            )}
                                          >
                                            <Eraser className="w-4 h-4" />
                                            <span className="text-sm">Remove Background</span>
                                          </button>

                                          <button
                                            type="button"
                                            onClick={() => setShowToolOptions('uncrop')}
                                            disabled={isProcessingTool}
                                            className={cn(
                                              "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors",
                                              isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                              isProcessingTool && "opacity-50 cursor-not-allowed"
                                            )}
                                          >
                                            <Maximize2 className="w-4 h-4" />
                                            <span className="text-sm">Uncrop</span>
                                            <ChevronRight className="w-3 h-3 ml-auto opacity-50" />
                                          </button>

                                          <button
                                            type="button"
                                            onClick={() => {
                                              setActiveToolMode({
                                                tool: 'replace-background',
                                                name: 'Replace Background',
                                                icon: <Layers className="w-4 h-4" />
                                              });
                                              setShowQuickTools(false);
                                              setShowToolOptions(null);
                                              setPrompt('');
                                              setTimeout(() => textareaRef.current?.focus(), 100);
                                            }}
                                            disabled={isProcessingTool}
                                            className={cn(
                                              "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors",
                                              isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                              isProcessingTool && "opacity-50 cursor-not-allowed"
                                            )}
                                          >
                                            <Layers className="w-4 h-4" />
                                            <span className="text-sm">Replace Background</span>
                                          </button>

                                          <button
                                            type="button"
                                            onClick={() => handleToolAction('remove-text')}
                                            disabled={isProcessingTool}
                                            className={cn(
                                              "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors",
                                              isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                              isProcessingTool && "opacity-50 cursor-not-allowed"
                                            )}
                                          >
                                            <Type className="w-4 h-4" />
                                            <span className="text-sm">Remove Text</span>
                                          </button>

                                          <button
                                            type="button"
                                            onClick={() => setShowToolOptions('commercial')}
                                            disabled={isProcessingTool}
                                            className={cn(
                                              "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors",
                                              isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                              isProcessingTool && "opacity-50 cursor-not-allowed"
                                            )}
                                          >
                                            <ImageIcon className="w-4 h-4" />
                                            <span className="text-sm">Commercial Photo</span>
                                            <ChevronRight className="w-3 h-3 ml-auto opacity-50" />
                                          </button>

                                          <button
                                            type="button"
                                            onClick={() => setShowToolOptions('upscale')}
                                            disabled={isProcessingTool}
                                            className={cn(
                                              "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors",
                                              isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100",
                                              isProcessingTool && "opacity-50 cursor-not-allowed"
                                            )}
                                          >
                                            <ZoomIn className="w-4 h-4" />
                                            <span className="text-sm">Upscale</span>
                                            <ChevronRight className="w-3 h-3 ml-auto opacity-50" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                    {attachedImages && attachedImages.length > 1 && (
                                      <div className={cn(
                                        "px-3 py-2 text-xs text-center border-t",
                                        isDarkMode ? "text-yellow-400/80 border-white/10" : "text-yellow-600 border-gray-200"
                                      )}>
                                        ⚠️ Use only 1 image with Tools
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className={cn(isDarkMode ? "border-t border-white/10" : "border-t border-gray-200")} />
                          <div className={cn(
                            "pl-4 pr-2 pt-2 pb-1 text-[11px] font-medium",
                            isDarkMode ? "text-white/60" : "text-gray-500"
                          )}>Aspect ratio</div>
                          <div className="p-2 pt-1 flex flex-col gap-1 max-h-40 overflow-y-auto aspect-ratio-container">
                            {aspectRatios.map((ratio) => (
                              <button
                                key={ratio.value}
                                type="button"
                                onClick={() => {
                                  onAspectRatioSelect(ratio.value);
                                  setShowLeftMenu(false);
                                  setShowQuickTools(false);
                                  setShowToolOptions(null);
                                }}
                                className={cn(
                                  "w-full flex items-center gap-3 px-3 py-1.5 rounded-xl text-left transition-colors",
                                  isDarkMode
                                    ? "text-white hover:bg-white/10"
                                    : "text-gray-900 hover:bg-gray-100",
                                  selectedAspectRatio === ratio.value
                                    ? (isDarkMode ? "bg-white/10" : "bg-gray-100")
                                    : ""
                                )}
                              >
                                <AspectIcon value={ratio.value} className="w-4 h-4" />
                                <span className="text-sm">({ratio.value})</span>
                                {selectedAspectRatio === ratio.value && (
                                  <span className={cn(
                                    "ml-auto text-[10px] px-2 py-0.5 rounded-full",
                                    isDarkMode ? "bg-white/10 text-white/80" : "bg-black/5 text-gray-600"
                                  )}>
                                    selected
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                          <div
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        onClick={onEnhancePrompt}
                        disabled={isEnhancingPrompt || !prompt.trim()}
                        className={cn(
                          "inline-flex gap-1.5 items-center justify-center whitespace-nowrap text-sm focus-visible:outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-default data-[disabled=true]:opacity-50 group/button relative px-3 py-2 h-9 rounded-full max-w-52 overflow-hidden font-semibold outline-none disabled:opacity-50",
                          isDarkMode ? "bg-white/10 hover:bg-white/15 text-white" : "bg-black/[0.04] hover:bg-black/[0.08] text-gray-900"
                        )}
                        title=""
                      >
                        {isEnhancingPrompt ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        <span className="text-xs font-medium">Enhance</span>
                      </Button>

                      {isRecording ? (
                        <Button
                          onClick={handleStopRecording}
                          className="h-9 w-9 rounded-xl p-0 flex items-center justify-center bg-red-600 hover:bg-red-700 transition-colors"
                        >
                          {isMicLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin text-white" />
                          ) : (
                            <div className="w-3 h-3 bg-white rounded-sm" />
                          )}
                        </Button>
                      ) : prompt.trim() ? (
                        <Button
                          onClick={handleSubmit}
                          disabled={isLocalSubmitting || (isEditMode ? isEditingImage : isGenerating)}
                          className={cn(
                            "h-9 w-9 rounded-full p-0 flex items-center justify-center transition-all duration-300",
                            isDarkMode
                              ? "bg-white text-black hover:bg-gray-100"
                              : "bg-gray-900 text-white hover:bg-gray-800"
                          )}
                        >
                          {isLocalSubmitting || (isEditMode ? isEditingImage : isGenerating) ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ArrowUp className="w-4 h-4" />
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={handleStartRecording}
                          className={cn(
                            'h-9 w-9 rounded-full p-0 flex items-center justify-center transition-all duration-200',
                            isDarkMode
                              ? 'bg-white/10 hover:bg-white/20 text-white'
                              : 'bg-black/[0.04] hover:bg-black/[0.08] text-[#303030]'
                          )}
                        >
                          {isMicLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Mic className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showCanvas && (
        <DrawCanvasModal
          isOpen={true}
          onClose={closeCanvas}
          onDone={(dataUrl) => handleCanvasDone(dataUrl)}
          isDarkMode={isDarkMode}
          initialImage={canvasInitialImage || undefined}
        />
      )}

      {/* Mobile Drawer for Options */}
      {isMobile && (
        <Drawer
          open={showMobileDrawer}
          onOpenChange={(open) => {
            if (!open) {
              closeMobileDrawer();
            }
          }}
        >
          <DrawerContent
            showHandle={false}
            className={`mx-auto flex w-full max-w-lg flex-col rounded-t-3xl border ${isDarkMode ? 'border-[#333] bg-[#1F1F1F]/95' : 'border-border/40 bg-background/95'} pb-3 shadow-2xl backdrop-blur-lg h-[55vh] sm:h-[50vh]`}
          >
            <div className="flex h-full flex-col">
              <div className="flex-1 overflow-y-auto overscroll-contain sm:px-5 px-4 pb-4 pt-2">
                {mobileDrawerView === 'main' ? (
                  <>
                    {/* Four action buttons in a row */}
                    <div className={`grid ${attachedImages && attachedImages.length > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-2 py-2`}>
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            onImageUpload(e);
                            closeMobileDrawer();
                          }}
                          className="hidden"
                          id="chitra-mobile-image-upload"
                        />
                        <label
                          htmlFor="chitra-mobile-image-upload"
                          className={`rounded-2xl py-3 flex flex-col items-center justify-center gap-2 text-foreground transition-all duration-150 active:scale-[0.98] cursor-pointer ${isDarkMode ? 'bg-[#3c3c3c] hover:bg-[#303030]' : 'bg-gray-200 hover:bg-gray-300 border border-gray-200'}`}
                        >
                          <span className="flex items-center justify-center">
                            <Upload className="w-[22px] h-[22px]" />
                          </span>
                          <span className="text-sm select-none">Upload</span>
                        </label>
                      </div>

                      <button
                        type="button"
                        onClick={() => { openCanvas(null, null); closeMobileDrawer(); }}
                        className={`rounded-2xl py-3 flex flex-col items-center justify-center gap-2 text-foreground transition-all duration-150 active:scale-[0.98] ${isDarkMode ? 'bg-[#3c3c3c] hover:bg-[#303030]' : 'bg-gray-200 hover:bg-gray-300 border border-gray-200'}`}
                      >
                        <span className="flex items-center justify-center">
                          <Paintbrush className="w-[22px] h-[22px]" />
                        </span>
                        <span className="text-sm select-none">Draw</span>
                      </button>

                      {onVideoModeClick && (
                        <button
                          type="button"
                          onClick={() => { setShowVideoModal(true); closeMobileDrawer(); }}
                          className={`rounded-2xl py-3 flex flex-col items-center justify-center gap-2 text-foreground transition-all duration-150 active:scale-[0.98] ${isDarkMode ? 'bg-[#3c3c3c] hover:bg-[#303030]' : 'bg-gray-200 hover:bg-gray-300 border border-gray-200'}`}
                        >
                          <span className="flex items-center justify-center">
                            <Video className="w-[22px] h-[22px]" />
                          </span>
                          <span className="text-sm select-none">Video</span>
                        </button>
                      )}

                      {attachedImages && attachedImages.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setMobileDrawerView('quicktools')}
                          className={`rounded-2xl py-3 flex flex-col items-center justify-center gap-2 text-foreground transition-all duration-150 active:scale-[0.98] ${isDarkMode ? 'bg-[#3c3c3c] hover:bg-[#303030]' : 'bg-gray-200 hover:bg-gray-300 border border-gray-200'}`}
                        >
                          <span className="flex items-center justify-center">
                            <Wand2 className="w-[22px] h-[22px]" />
                          </span>
                          <span className="text-sm select-none">Tools</span>
                        </button>
                      )}
                    </div>

                    <hr className={`my-1 ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`} />

                    {/* Aspect ratios */}
                    <div className={cn(
                      "pl-2 pr-2 pt-2 pb-1 text-[11px] font-medium",
                      isDarkMode ? "text-white/60" : "text-gray-500"
                    )}>Aspect ratio</div>
                    <div className="flex flex-col gap-1 max-h-[100%] overflow-y-auto">
                      {aspectRatios.map((ratio) => (
                        <button
                          key={ratio.value}
                          type="button"
                          onClick={() => {
                            onAspectRatioSelect(ratio.value);
                            closeMobileDrawer();
                          }}
                          className={cn(
                            "w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150",
                            isDarkMode
                              ? `hover:bg-white/5 active:bg-white/10 ${selectedAspectRatio === ratio.value ? 'bg-white/10' : ''}`
                              : `hover:bg-gray-200 active:bg-gray-300 ${selectedAspectRatio === ratio.value ? 'bg-gray-200' : ''}`
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <AspectIcon value={ratio.value} className="w-5 h-5" />
                            <span className="text-sm">{ratio.label}</span>
                          </div>
                          {selectedAspectRatio === ratio.value && <Check className={`w-5 h-5 ${isDarkMode ? 'text-[#4EA3FF]' : 'text-[#007AFF]'}`} />}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Quick Tools View */}
                    <button
                      type="button"
                      onClick={() => setMobileDrawerView('main')}
                      className={cn(
                        "flex items-center gap-3 px-2 py-3 rounded-xl text-left transition-colors mb-2",
                        isDarkMode ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-100"
                      )}
                    >
                      <ChevronLeft className="w-5 h-5" />
                      <span className="text-sm font-medium">Back</span>
                    </button>

                    <hr className={`my-1 ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`} />

                    <div className="flex flex-col gap-0.5 mt-2">
                      <button
                        type="button"
                        onClick={() => { handleToolAction('remove-bg'); closeMobileDrawer(); }}
                        disabled={isProcessingTool}
                        className={cn(
                          "w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150",
                          isDarkMode ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-200 active:bg-gray-300',
                          isProcessingTool && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <Eraser className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                          <span className="text-sm">Remove Background</span>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => { handleToolAction('uncrop', { extend_left: 200, extend_right: 200, extend_up: 200, extend_down: 200 }); closeMobileDrawer(); }}
                        disabled={isProcessingTool}
                        className={cn(
                          "w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150",
                          isDarkMode ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-200 active:bg-gray-300',
                          isProcessingTool && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <Maximize2 className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                          <span className="text-sm">Uncrop</span>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setActiveToolMode({
                            tool: 'replace-background',
                            name: 'Replace Background',
                            icon: <Layers className="w-4 h-4" />
                          });
                          closeMobileDrawer();
                          setPrompt('');
                          setTimeout(() => textareaRef.current?.focus(), 100);
                        }}
                        disabled={isProcessingTool}
                        className={cn(
                          "w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150",
                          isDarkMode ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-200 active:bg-gray-300',
                          isProcessingTool && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <Layers className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                          <span className="text-sm">Replace Background</span>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => { handleToolAction('remove-text'); closeMobileDrawer(); }}
                        disabled={isProcessingTool}
                        className={cn(
                          "w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150",
                          isDarkMode ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-200 active:bg-gray-300',
                          isProcessingTool && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <Type className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                          <span className="text-sm">Remove Text</span>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => { handleToolAction('upscale', { target_width: 2048, target_height: 2048 }); closeMobileDrawer(); }}
                        disabled={isProcessingTool}
                        className={cn(
                          "w-full flex items-center justify-between py-3 px-2 rounded-xl text-foreground transition-colors duration-150",
                          isDarkMode ? 'hover:bg-white/5 active:bg-white/10' : 'hover:bg-gray-200 active:bg-gray-300',
                          isProcessingTool && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <ZoomIn className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                          <span className="text-sm">Upscale (2K)</span>
                        </div>
                      </button>
                    </div>

                    {attachedImages && attachedImages.length > 1 && (
                      <div className={cn(
                        "px-3 py-2 text-xs text-center border-t mt-2",
                        isDarkMode ? "text-yellow-400/80 border-white/10" : "text-yellow-600 border-gray-200"
                      )}>
                        ⚠️ Use only 1 image with Tools
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      )}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/70 z-[1000] flex items-center justify-center p-4" onClick={() => setShowVideoModal(false)}>
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold mb-4 text-[#303030] text-center">Exclusive Feature</h3>
              <div className="flex items-center gap-4 mb-4">
                <p className="text-gray-600 flex-1">Video Mode is only available for Exclusive members</p>
                <video
                  src="https://res.cloudinary.com/dx0aqgtyg/video/upload/v1763025965/oreogmembers.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-16 h-16 rounded-lg object-cover bg-gray-100"
                />
              </div>
              <button
                onClick={() => setShowVideoModal(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChitraInputArea;

